import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { mangas, categories } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const filteredMangas = slug === 'all' || !slug
    ? mangas
    : mangas.filter((m) => m.category === slug);

  return (
    <>
      <ScrollToTop />
      <Header />

      <main className="pt-20">
        <div className="bg-background-100 py-6">
          <div className="max-w-screen-2xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
            <div className="px-4 md:px-6 mt-4">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {currentCategory.name}漫画
              </h1>
              <p className="text-sm text-foreground-500">
                {currentCategory.description} · 共 {filteredMangas.length} 部作品
              </p>
            </div>
          </div>
        </div>

        <section className="py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredMangas.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无漫画作品</p>
                <Link to="/category/all" className="text-primary-600 hover:underline text-sm mt-2 inline-block">
                  浏览全部漫画
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredMangas.map((manga) => (
                  <Link
                    key={manga.id}
                    to={`/manga/${manga.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={manga.cover}
                        alt={`${manga.title}漫画`}
                        title={`${manga.title} - 在线免费阅读`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium text-white ${manga.status === '已完结' ? 'bg-accent-500' : 'bg-primary-500'}`}>
                          {manga.status}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <p className="text-white text-xs">
                          <StarDisplay rating={manga.rating} /> {manga.rating.toFixed(1)}
                        </p>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                      {manga.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">
                      {manga.author} · 更新至{manga.chapters.length}话
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${currentCategory.name}漫画 - 洞洞杂货铺漫画`,
          description: currentCategory.description,
          url: `https://www.178mh.com/category/${slug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '洞洞杂货铺漫画',
          },
          numberOfItems: filteredMangas.length,
        })}
      </script>
    </>
  );
}

function StarDisplay({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`text-[8px] ${i < Math.floor(rating) ? 'ri-star-fill text-accent-400' : i < rating ? 'ri-star-half-fill text-accent-400' : 'ri-star-fill text-white/30'}`}
        ></i>
      ))}
    </span>
  );
}